/** Fase 4 — início e evolução do atleta, a partir de dados persistidos (`/me/dashboard`). */
import { useState } from "react";
import { api } from "../../api/client";
import type { AthleteDashboard, AthleteDetail, Records } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { BarChart, ChartFrame, TimeChart } from "../../components/charts";
import { WeightReminder } from "../../components/body";
import {
  rate,
  EvolutionCharts,
  GoalsPanel,
  goalValue,
  Kpi,
  PeriodFilters,
  RacesPanel,
  RecordsGrid,
  WellnessCheckin,
  type EvolutionFilters,
} from "../../components/performance";
import { EmptyState, ErrorState, Loading, PageHead } from "../../components/ui";
import { distanceLabel, formatDate, formatDuration, formatPace, metersToKm, paceOf, paceShort } from "../../lib/format";
import { addDays, iso } from "../../lib/training";
import { useAsync } from "../../lib/useAsync";
import { useCaptureContext } from "../../lib/useGpsRecorder";
import { Link } from "../../router/router";
import { StrengthOverviewSection } from "../../components/StrengthOverview";
import { WeekWorkouts } from "./AthleteWorkouts";
import { LOCALE, t, tn } from "../../i18n";

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? t("Bom dia") : h < 18 ? t("Boa tarde") : t("Boa noite");
}

export function DashboardKpis({ d }: { d: AthleteDashboard }) {
  const c = d.cards;
  const g = c.weekly_goal;
  const gp = g?.progress?.athletes[0];
  return (
    <dl className="kpis">
      <Kpi
        label={t("Distância na semana")}
        value={metersToKm(c.week_distance_m)}
        sub={c.week_planned_distance_m ? t("de {planned} planejados · {runs}", { planned: metersToKm(c.week_planned_distance_m), runs: tn("{n} corrida", "{n} corridas", c.week_sessions) }) : tn("{n} corrida", "{n} corridas", c.week_sessions)}
        meter={c.week_planned_distance_m ? c.week_distance_m / c.week_planned_distance_m : null}
      />
      <Kpi label={t("Distância no mês")} value={metersToKm(c.month_distance_m)} />
      <Kpi label={t("Corridas no período")} value={String(c.period_activities)} sub={t("{a} a {b}", { a: formatDate(d.period.start), b: formatDate(d.period.end) })} />
      <Kpi
        label={t("Treinos concluídos (4 semanas)")}
        value={c.completion_rate_4w != null ? rate(c.completion_rate_4w) : "—"}
        sub={c.completion_due_4w ? t("{a} de {b} treinos", { a: Math.round((c.completion_rate_4w ?? 0) * c.completion_due_4w), b: c.completion_due_4w }) : t("Sem treinos publicados")}
      />
      {g && gp && <Kpi label={t("Meta: {title}", { title: g.title })} value={goalValue(g.metric, gp.value)} sub={t("de {target_value} nesta semana", { target_value: goalValue(g.metric, g.target_value) })} meter={gp.pct} />}
    </dl>
  );
}

export function AthleteHomePage({ orgId }: { orgId: string }) {
  const capture = useCaptureContext();
  const { membership } = useAuth();
  const me = useAsync(() => api.get<AthleteDetail>(`/orgs/${orgId}/me/athlete`), [orgId]);
  const dash = useAsync(() => api.get<AthleteDashboard>(`/orgs/${orgId}/me/dashboard`), [orgId]);
  if ((me.loading && !me.data) || (dash.loading && !dash.data)) return <Loading />;
  if (me.error) return <ErrorState error={me.error} onRetry={me.reload} />;
  if (dash.error) return <ErrorState error={dash.error} onRetry={dash.reload} />;
  const a = me.data!;
  const d = dash.data!;
  const base = `/org/${orgId}/atleta`;
  const weeks = d.summary.weeks;
  const paced = d.summary.points.filter((p) => p.pace_s_per_km);
  const marks = [...(a.profile?.best_marks ?? [])].sort((x, y) => x.distance_m - y.distance_m);
  return (
    <>
      <PageHead
        title={`${greeting()}, ${a.full_name.split(" ")[0]}`}
        subtitle={[membership?.organization_name, a.coach && t("Treinador(a): {name}", { name: a.coach.name }), a.group && t("Grupo: {name}", { name: a.group.name })].filter(Boolean).join(". ")}
        actions={capture.allowed ? <Link to={`/org/${orgId}/atleta/correr`} className="btn btn-primary">{t("Gravar corrida")}</Link> : undefined}
      />
      <DashboardKpis d={d} />

      {d.pending.length > 0 && (
        <section className="panel section" aria-labelledby="pendencias">
          <div className="panel-head">
            <h2 id="pendencias">{t("Pendências")}</h2>
          </div>
          <ul className="list-plain pending-list">
            {d.pending.map((p) => (
              <li key={p.kind}>
                {p.kind === "missing_rpe" && p.activity_id ? <Link to={`${base}/atividades/${p.activity_id}`}>{p.message}</Link> : p.kind === "unregistered_workouts" ? <Link to={`${base}/calendario`}>{p.message}</Link> : p.message}
              </li>
            ))}
          </ul>
        </section>
      )}

      <WeightReminder orgId={orgId} />

      <WellnessCheckin orgId={orgId} today={d.wellness.today} onSaved={dash.reload} />

      <WeekWorkouts orgId={orgId} />

      <section className="section" aria-labelledby="evolucao-resumo">
        <div className="row-between">
          <h2 id="evolucao-resumo">{t("Sua evolução")}</h2>
          <Link to={`${base}/evolucao`}>{t("Ver evolução completa")}</Link>
        </div>
        <div className="chart-grid" style={{ marginTop: 10 }}>
          <ChartFrame
            title={t("Quilometragem por semana")}
            subtitle={t("Últimas 12 semanas: realizado e planejado.")}
            legend={[{ label: t("Realizado"), color: "var(--chart-1)" }, { label: t("Planejado"), color: "var(--chart-ref)", kind: "line" }]}
            table={{ head: [t("Semana"), t("Realizado"), t("Planejado")], rows: weeks.map((w) => [formatDate(w.week_start), metersToKm(w.distance_m), w.planned_distance_m ? metersToKm(w.planned_distance_m) : "—"]) }}
            empty={weeks.every((w) => !w.distance_m && !w.planned_distance_m) ? t("Importe suas corridas para ver o volume semanal.") : null}
          >
            <BarChart
              data={weeks.map((w) => ({ key: w.week_start, label: `${w.week_start.slice(8, 10)}/${w.week_start.slice(5, 7)}`, title: t("Semana de {week_start}", { week_start: formatDate(w.week_start) }), value: w.distance_m || null, ref: w.planned_distance_m || null }))}
              fmt={(v) => metersToKm(v)}
              axisFmt={(v) => (v / 1000).toLocaleString(LOCALE, { maximumFractionDigits: 0 })}
              valueLabel={t("realizado")}
              refLabel={t("planejado")}
              labelEvery={2}
            />
          </ChartFrame>
          <ChartFrame
            title={t("Ritmo médio por corrida")}
            subtitle={t("Mais rápido fica em cima; a linha é a média das últimas 5 corridas.")}
            table={{ head: [t("Data"), t("Distância"), t("Ritmo")], rows: paced.map((p) => [formatDate(p.date), metersToKm(p.distance_m), formatPace(p.pace_s_per_km)]) }}
            empty={paced.length === 0 ? t("Importe corridas com distância para ver o ritmo.") : null}
          >
            <TimeChart
              points={paced.map((p) => ({ key: p.activity_id, date: p.date, value: p.pace_s_per_km!, title: `${formatDate(p.date)} · ${p.name}`, extra: [metersToKm(p.distance_m)] }))}
              fmt={paceShort}
              valueLabel="min/km"
              invert
              trend
              from={d.period.start}
              to={d.period.end}
            />
          </ChartFrame>
        </div>
      </section>

      <div className="chart-grid section">
        <section aria-labelledby="proximas-provas">
          <h2 id="proximas-provas">{t("Próximas provas")}</h2>
          {d.upcoming_races.length === 0 ? (
            <div className="panel" style={{ marginTop: 10 }}>
              <EmptyState title={t("Nenhuma prova marcada")} action={<Link to={`${base}/evolucao#provas`}>{t("Cadastrar prova")}</Link>} />
            </div>
          ) : (
            <div className="panel" style={{ marginTop: 10 }}>
              <ul className="list-plain">
                {d.upcoming_races.map((r) => (
                  <li key={r.id} className="row-between">
                    <span>
                      <span className={`prio ${r.priority}`}>{r.priority}</span> <strong>{r.name}</strong>
                      <span className="muted"> · {metersToKm(r.distance_m)}</span>
                    </span>
                    <span className="figs">
                      {formatDate(r.race_date)}
                      {r.target_time_s && <span className="muted"> · {t("objetivo {v}", { v: formatDuration(r.target_time_s) })}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
        <section aria-labelledby="ultimas">
          <h2 id="ultimas">{t("Últimos treinos")}</h2>
          <div className="panel" style={{ marginTop: 10 }}>
            {d.recent_activities.length === 0 ? (
              <EmptyState title={t("Nenhuma atividade ainda")} action={<Link to={`${base}/atividades`}>{t("Importar atividade")}</Link>} />
            ) : (
              <ul className="list-plain list-click">
                {d.recent_activities.map((r) => (
                  <li key={r.id} className="recent-item">
                    <Link to={`${base}/atividades/${r.id}`}>{r.name}</Link>
                    <span className="figs muted">
                      {formatDate(r.local_date)} · {metersToKm(r.distance_m)} · {formatPace(r.pace_s_per_km)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <section className="section" aria-labelledby="recordes">
        <h2 id="recordes">{t("Recordes pessoais")}</h2>
        <p className="muted" style={{ marginBottom: 10 }}>
          {t("Calculados das suas atividades e provas.")}
        </p>
        {d.records.length === 0 && marks.length > 0 ? (
          <dl className="records">
            {marks.map((m, i) => (
              <Kpi key={i} label={`${distanceLabel(m.distance_m)} (informado)`} value={formatDuration(m.time_s)} sub={formatPace(paceOf(m.time_s, m.distance_m))} />
            ))}
          </dl>
        ) : (
          <RecordsGrid records={d.records} />
        )}
      </section>
    </>
  );
}

export function useEvolution(path: string) {
  const [filters, setFilters] = useState<EvolutionFilters>({ weeks: 12, sport: "run" });
  const [bucket, setBucket] = useState("");
  const to = new Date();
  const monday = addDays(to, -((to.getDay() + 6) % 7));
  const from = iso(addDays(monday, -7 * (filters.weeks - 1)));
  // "Força" não usa o painel de corrida: a API só conhece run|all, então o pedido não muda ao alternar corrida ↔ força
  const apiSport = filters.sport === "all" ? "all" : "run";
  const dash = useAsync(() => api.get<AthleteDashboard>(path, { from, to: iso(to), sport: apiSport }), [path, from, apiSport]);
  return { filters, setFilters, bucket, setBucket, dash };
}

export function AthleteEvolutionPage({ orgId }: { orgId: string }) {
  const { filters, setFilters, bucket, setBucket, dash } = useEvolution(`/orgs/${orgId}/me/dashboard`);
  const records = useAsync(() => api.get<Records>(`/orgs/${orgId}/me/records`), [orgId]);
  const d = dash.data;
  const strengthOnly = filters.sport === "strength";
  return (
    <>
      <PageHead title={t("Evolução")} subtitle={t("Volume, ritmo, carga e marcas calculados a partir das suas atividades importadas.")} />
      <PeriodFilters value={filters} onChange={setFilters} bucket={bucket} onBucket={setBucket} />
      {!strengthOnly && dash.error && <ErrorState error={dash.error} onRetry={dash.reload} />}
      {!strengthOnly && !d && dash.loading && <Loading />}
      {!strengthOnly && d && (
        <div className={dash.loading ? "refetching" : undefined}>
          <DashboardKpis d={d} />
          <div className="section">
            <EvolutionCharts dash={d} bucket={bucket} records={records.data} />
          </div>
          <GoalsPanel orgId={orgId} goals={d.goals} athleteId={d.athlete_id} canManage={false} onChanged={dash.reload} />
        </div>
      )}
      {filters.sport !== "run" && <StrengthOverviewSection path={`/orgs/${orgId}/me/strength/overview`} weeks={filters.weeks} />}
      {!strengthOnly && <RacesPanel orgId={orgId} self />}
      {!strengthOnly && (
        <section className="section" aria-labelledby="recordes-ev">
          <h2 id="recordes-ev">{t("Recordes atuais")}</h2>
          <div style={{ marginTop: 10 }}>{records.data && <RecordsGrid records={records.data.current} />}</div>
        </section>
      )}
    </>
  );
}
